<script lang="ts" setup>
  import { useToast } from '@/components/ui/toast'
  import { useTranslation } from '@/composables/useTranslation'
  import { FILE_INPUT_ACCEPT, isFileSupported } from '@/constants/documentFileTypes'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import type { CustomerCase } from '@/types/generated'
  import { Upload } from 'lucide-vue-next'
  import type { PropType } from 'vue'
  import { defineEmits, defineProps, ref } from 'vue'

  const emit = defineEmits(['fileUploaded', 'filesSelected'])

  const props = defineProps({
    caseData: {
      type: Object as PropType<CustomerCase>,
      required: true
    },
    maxSize: {
      type: Number,
      default: 100
    },
    showUploadingList: {
      type: Boolean,
      default: false
    }
  })

  const { toast } = useToast()

  const { t } = useTranslation({
    en: {
      DropFilesHere: 'Drop your files here or',
      Browse: 'browse',
      MaximumSize: 'Maximum size',
      Uploading: 'Uploading...',
      UnsupportedFileType: 'Unsupported file type'
    },
    de: {
      DropFilesHere: 'Legen Sie Ihre Datei hier ab oder',
      Browse: 'durchsuchen',
      MaximumSize: 'Maximale Größe',
      Uploading: 'Wird hochgeladen...',
      UnsupportedFileType: 'Nicht unterstützter Dateityp'
    }
  })

  const caseStore = useCaseStore()
  const fileInput = ref<HTMLInputElement | null>(null)
  const dropZone = ref<HTMLDivElement | null>(null)

  // === UPLOAD STATE ===
  const isUploading = ref(false)
  const uploadingFiles = ref<string[]>([])

  // === EVENT HANDLERS ===

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    const files = e.dataTransfer?.files
    if (!files) return

    const allFiles = Array.from(files)
    const supported = allFiles.filter((f) => isFileSupported(f))
    const rejected = allFiles.filter((f) => !isFileSupported(f))

    if (rejected.length > 0) {
      toast({
        title: t('UnsupportedFileType'),
        description: rejected.map((f) => f.name).join(', '),
        variant: 'destructive'
      })
    }

    if (supported.length > 0) {
      handleFiles(supported)
    }
  }

  const onFileInput = (e: Event) => {
    const input = e.target as HTMLInputElement
    if (input.files) {
      handleFiles(Array.from(input.files))
    }
  }

  // === FILE PROCESSING ===

  const handleFiles = async (files: File[]) => {
    // Filter valid files
    const validFiles = files.filter((file) => {
      const isValidSize = file.size <= props.maxSize * 1024 * 1024
      if (!isValidSize) {
        console.warn(
          `File ${file.name} is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB)`
        )
      }
      return isValidSize
    })

    if (validFiles.length === 0) return

    // Emit files selected event
    emit('filesSelected', validFiles)

    isUploading.value = true
    uploadingFiles.value = validFiles.map((f) => f.name)

    // Upload each file using the document processing module
    const uploadPromises = validFiles.map(async (file) => {
      try {
        const response = await caseStore.documentProcessor.uploadDocument(file)

        if (response.uuid) {
          console.log('Document uploaded successfully:', response.uuid)
          emit('fileUploaded', response.uuid)
        }

        return response
      } catch (error) {
        console.error('Upload failed for file:', file.name, error)
        throw error
      }
    })

    // Wait for all uploads to complete
    try {
      await Promise.all(uploadPromises)
      console.log('All uploads completed')
    } catch (error) {
      console.error('Some uploads failed:', error)
    } finally {
      isUploading.value = false
      uploadingFiles.value = []
    }
  }

  // === DRAG & DROP HANDLERS ===

  const preventDefaults = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
  }
</script>

<template>
  <div
    ref="dropZone"
    class="flex cursor-pointer justify-center rounded-xl border border-dashed border-gray-300 bg-white p-6"
    @dragover.prevent="preventDefaults"
    @dragenter.prevent="preventDefaults"
    @dragleave.prevent="preventDefaults"
    @drop="onDrop"
    @click="!isUploading && fileInput?.click()"
  >
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      multiple
      :accept="FILE_INPUT_ACCEPT"
      :disabled="isUploading"
      @change="onFileInput"
    />
    <div
      v-if="!isUploading"
      class="text-center"
    >
      <span
        class="inline-flex size-12 items-center justify-center rounded-full bg-gray-100 text-gray-800"
      >
        <Upload class="size-5 shrink-0" />
      </span>

      <div class="mt-2 flex flex-wrap justify-center text-sm leading-6 text-gray-600">
        <span class="pe-1 font-medium text-gray-800">{{ t('DropFilesHere') }}</span>
        <span
          class="rounded-lg bg-white font-semibold text-purple-600 decoration-2 focus-within:outline-none
            focus-within:ring-2 focus-within:ring-purple-600 focus-within:ring-offset-2 hover:text-purple-700
            hover:underline"
        >
          {{ t('Browse') }}
        </span>
      </div>

      <p class="mt-1 text-xs text-gray-400">{{ t('MaximumSize') }} {{ props.maxSize }} MB.</p>
    </div>
    <div
      v-else
      class="text-center"
    >
      <svg
        class="mx-auto h-8 w-8 animate-spin text-purple-700"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
      <p class="mt-2 text-sm font-medium text-purple-700">{{ t('Uploading') }}</p>
      <p
        v-if="showUploadingList && uploadingFiles.length"
        class="mt-1 text-xs text-gray-500"
      >
        {{ uploadingFiles.join(', ') }}
      </p>
    </div>
  </div>
</template>
